import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TransitionGroup } from "react-transition-group";

import { colorsPalettes } from "@similarweb/styles";

import { genericElementsAreInTheSameOrder } from "app/pages/sales-intelligence/utils/arrays";

import type { GetColumnPluginsForSlot } from "../../plugins/types";
import type { SearchableSelectionOption } from "../../types";
import { ColumnOptionDragLayer } from "../ColumnOptionDragLayer/ColumnOptionDragLayer";
import { ColumnOptionFadeTransition } from "../ColumnOptionFadeTransition/ColumnOptionFadeTransition";
import { DraggableColumnOptionFadeTransition } from "../ColumnOptionFadeTransition/DraggableColumnOptionFadeTransition";
import { SelectedColumnOptionView } from "../ColumnOptionView/ColumnOptionView";

import { StyledColumnOptionsList, StyledColumnOptionsListWrapper, StyledColumnOptionsTrack } from "./styles";

type Props<O> = {
    columnOptions: readonly O[];
    getColumnPluginsForSlot?: GetColumnPluginsForSlot;
    onColumnOptionClick: (columnName: string) => void;
    onColumnOptionReorder: (columnNames: readonly string[]) => void;
};

export const SelectedColumnOptionsList = <O extends SearchableSelectionOption>(props: Props<O>) => {
    const { columnOptions, onColumnOptionClick, onColumnOptionReorder, getColumnPluginsForSlot } = props;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [parentHasChildDragging, setParentHasChildDragging] = useState(false);
    const handleEntering = (node: HTMLElement | null) => {
        setTimeout(() => node?.scrollIntoView?.({ behavior: "smooth", block: "center" }), 300);
    };

    const handleEnter = (node: HTMLElement) => {
        node.firstElementChild?.animate(
            {
                backgroundColor: [colorsPalettes.blue[100], colorsPalettes.blue[100], colorsPalettes.carbon[25]],
                offset: [0, 0.8, 1],
                easing: "ease-in-out",
            },
            1000,
        );
    };

    const [columns, setColumns] = useState(columnOptions);

    useEffect(() => {
        if (columnOptions.length !== columns.length) {
            setColumns(columnOptions);
            onColumnOptionReorder(columnOptions.map((c) => c.id));
        }
    }, [columnOptions]);

    useEffect(() => {
        if (
            onColumnOptionReorder &&
            !genericElementsAreInTheSameOrder(columns, columnOptions, (a, b) => a.id === b.id)
        ) {
            onColumnOptionReorder(columns.map((c) => c.id));
        }
    }, [columns]);

    const findCol = useCallback(
        (name: string) => {
            const index = columns.findIndex((c) => c.id === name);
            return {
                col: columns[index],
                index,
            };
        },
        [columns],
    );

    const moveCol = useCallback(
        (id: string, atIndex: number) => {
            const { col, index } = findCol(id);

            if (index === -1) {
                return;
            }

            const updatedColumns = [...columns];
            updatedColumns.splice(index, 1); // Remove card from the given index
            updatedColumns.splice(atIndex, 0, col);
            setColumns(updatedColumns);
        },
        [findCol, columns, setColumns],
    );

    const componentToRender = useMemo(() => {
        return (column: O) => {
            return !column.isStatic ? DraggableColumnOptionFadeTransition : ColumnOptionFadeTransition;
        };
    }, []);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        if (parentHasChildDragging) {
            containerRef.current.className += " dragging";
        } else {
            containerRef.current.offsetWidth;
            containerRef.current.className = containerRef.current.className.replace(/dragging/gi, "");
        }
    }, [parentHasChildDragging]);

    return (
        <StyledColumnOptionsList ref={containerRef}>
            <ColumnOptionDragLayer findCol={findCol} />
            <StyledColumnOptionsListWrapper>
                <TransitionGroup component={StyledColumnOptionsTrack}>
                    {columns.map((column) => {
                        const ComponentToRender = componentToRender(column);

                        const badgePlugins =
                            getColumnPluginsForSlot?.("column-badge", { column, isSelected: true }) ?? [];

                        return (
                            <ComponentToRender
                                key={column.id}
                                id={column?.id}
                                onEntering={handleEntering}
                                onEnter={handleEnter}
                                moveCol={moveCol}
                                findCol={findCol}
                                setParentHasChildDraggin={setParentHasChildDragging}
                            >
                                <SelectedColumnOptionView
                                    {...column}
                                    key={column.id}
                                    onClick={() => onColumnOptionClick(column.id)}
                                    isDraggable={!column.isStatic}
                                    badgePlugins={badgePlugins}
                                />
                            </ComponentToRender>
                        );
                    })}
                </TransitionGroup>
            </StyledColumnOptionsListWrapper>
        </StyledColumnOptionsList>
    );
};
